import type { RovoModifiedObject } from '@atlassian/conversation-assistant-instrumentation/types/analytics';

type PatternEntry = {
	/** Regex tested against `pathname` (or `pathname + search` for query-param patterns). */
	pattern: RegExp;
	objectType: RovoModifiedObject['objectType'];
	/**
	 * When true, the pattern is tested against `pathname + search` instead of
	 * just `pathname`, to support query-param based object IDs.
	 */
	includeSearch?: boolean;
};

/**
 * Ordered list of URL patterns for recognized Atlassian content objects.
 * Each entry maps a regex (with one capture group for the object ID) to an
 * objectType. The first matching entry wins.
 */
const URL_PATTERNS: PatternEntry[] = [
	// Jira issue: /browse/PROJ-123
	{
		pattern: /\/browse\/([A-Z][A-Z0-9_]+-\d+)/,
		objectType: 'work-item',
	},
	// Jira project board with selected issue: /jira/.../projects/...?selectedIssue=PROJ-123
	{
		pattern: /\/jira\/.+\/projects\/.+[?&]selectedIssue=([A-Z][A-Z0-9_]+-\d+)/,
		objectType: 'work-item',
		includeSearch: true,
	},
	// Confluence page. Supports view and editor URL shapes:
	//   /wiki/spaces/SPACE/pages/123456
	//   /wiki/spaces/SPACE/pages/123456/Page+Title
	//   /wiki/spaces/SPACE/pages/edit-v2/123456     (editor)
	//   /wiki/spaces/SPACE/pages/live-edit/123456   (live editor)
	// The optional non-capturing group only matches a non-numeric mode segment,
	// so it can never accidentally consume the page ID itself.
	{
		pattern: /\/wiki\/spaces\/[^/]+\/pages\/(?:[a-z][a-z0-9-]*\/)?(\d+)/i,
		objectType: 'page',
	},
	// Confluence blog post. Similar to pages but with /blog/ instead of /pages/
	{
		pattern: /\/wiki\/spaces\/[^/]+\/blog\/(?:[a-z][a-z0-9-]*\/)?(\d+)/i,
		objectType: 'blog',
	},
	/**
	 * NOTE: This methodology does not work for Confluence whiteboards, slides, databases (or anything rendered in an iframe) because events cannot bubble from within iframes due to cross-origin restrictions.
	 * Whiteboard and database teams to log this information themselves.
	 */
	// Bitbucket pull request: /repos/<repo>/pull-requests/123 or /pull-requests/123
	{
		pattern: /\/pull-requests\/(\d+)/,
		objectType: 'pull-request',
	},
];

/**
 * Parses the current page URL and returns a RovoModifiedObject if the URL
 * matches a known Atlassian product page where an object could be adopted.
 *
 * Returns null for unrecognized URLs — in which case no adopted event is fired.
 */
export function parseRAWTrackedUrls(href: string): RovoModifiedObject | null {
	try {
		const url = new URL(href);

		for (const { pattern, objectType, includeSearch } of URL_PATTERNS) {
			const subject = includeSearch ? url.pathname + url.search : url.pathname;
			const match = subject.match(pattern);
			if (match) {
				return { objectType, objectId: match[1] };
			}
		}

		return null;
	} catch {
		return null;
	}
}
